import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Menubar } from "primereact/menubar";
import { Menu } from "primereact/menu";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { MobXProviderContext, observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AFH_LOGO, ONE_HUB_FOR_CARE_LOGO } from "../../assets";
import { RouterAddress } from "../../constants/RouterAddress";
import { USER_GROUP_ADMIN, getErrorTitle } from "../../constants/constants";
import { getSessionBrandOverride } from "../../features/auth/config/brandOverride";
import { useAuthExperience } from "../../features/auth/hooks/useAuthExperience";
import { BRAND_KEY_ONE_HUB_FOR_CARE } from "../../constants/brandConstants";
import { useResponsive } from "../../responsive/ResponsiveProvider";
import { redirectToHomeForAuthMode } from "../../utils/stringUtils";
import {
  ROLE_TYPE_CAREGIVER,
  ROLE_TYPE_FACILITY_WORKER,
  ROLE_TYPE_MHP,
  ROLE_TYPE_NURSE,
  ROLE_TYPE_PROVIDER,
} from "../../constants/RegisterConstants";
import { buildInviteMessage } from "../../constants/inviteFeatures";
import { useTour } from "../tour/TourProvider";
import { nurseTutorialOptions, providerTutorialOptions, TourId } from "../tour/providerOnboardingTour";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  renderSurfaceHeader,
} from "../../components/general/uiSurfaceStyles";

const formatRoleLabel = (roleType: string) => {
  if (!roleType) {
    return "Guest Access";
  }

  if (roleType === ROLE_TYPE_NURSE) {
    return "Registered Nurse";
  }
  if (roleType === ROLE_TYPE_MHP) {
    return "Mental Health Professional";
  }

  return roleType
    .replace(/_/g, " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatHeaderActAsLabel = (roleType: string) => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase();
  if (!normalizedRoleType) {
    return "Guest";
  }
  if (normalizedRoleType === ROLE_TYPE_NURSE) {
    return "Nurse";
  }
  if (normalizedRoleType === ROLE_TYPE_MHP) {
    return "MHP";
  }

  return formatRoleLabel(normalizedRoleType);
};

const canUseConnectionsAndInvites = (roleType: string) => (
  roleType === ROLE_TYPE_PROVIDER
  || roleType === ROLE_TYPE_NURSE
  || roleType === ROLE_TYPE_CAREGIVER
  || roleType === ROLE_TYPE_MHP
);

const canUseSharedCalendar = (roleType: string, isAdminUser: boolean) => (
  isAdminUser
  || roleType === ROLE_TYPE_PROVIDER
  || roleType === ROLE_TYPE_CAREGIVER
  || roleType === ROLE_TYPE_NURSE
  || roleType === ROLE_TYPE_MHP
);

const canUseHeaderHome = (roleType: string) => (
  canUseConnectionsAndInvites(roleType) || roleType === ROLE_TYPE_FACILITY_WORKER
);

const canUseSharedContent = (roleType: string) => roleType !== ROLE_TYPE_FACILITY_WORKER;

const SUPPORTED_PUBLIC_ROLE_QUERY_TYPES = new Set([
  ROLE_TYPE_PROVIDER,
  ROLE_TYPE_NURSE,
  ROLE_TYPE_CAREGIVER,
  ROLE_TYPE_MHP,
]);

const appendRoleQueryParam = (path: string, roleType: string) => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase();
  if (!SUPPORTED_PUBLIC_ROLE_QUERY_TYPES.has(normalizedRoleType)) {
    return path;
  }

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}role=${encodeURIComponent(normalizedRoleType)}`;
};

const getHeaderBillingRoleType = (pathname: string, fallbackRoleType: string) => {
  const normalizedPath = String(pathname || "").toLowerCase();
  if (normalizedPath.startsWith("/provider/")) {
    return ROLE_TYPE_PROVIDER;
  }
  if (normalizedPath.startsWith("/nurse/")) {
    return ROLE_TYPE_NURSE;
  }
  return fallbackRoleType;
};

const getHeaderRoleHomePath = (authMode: string, roleType: string) => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase();
  if (!normalizedRoleType) {
    return RouterAddress.APP_ROOT;
  }
  if (normalizedRoleType === ROLE_TYPE_PROVIDER) {
    return RouterAddress.PROVIDER_HOME;
  }
  if (normalizedRoleType === ROLE_TYPE_NURSE) {
    return RouterAddress.NURSE_HOME;
  }
  if (normalizedRoleType === ROLE_TYPE_CAREGIVER) {
    return RouterAddress.CAREGIVER_HOME;
  }
  if (normalizedRoleType === ROLE_TYPE_MHP) {
    return RouterAddress.MHP_HOME;
  }
  return redirectToHomeForAuthMode(authMode, normalizedRoleType);
};

type HeaderWorkspaceOption = {
  label: string;
  value: string;
  roleType?: string;
  mode: "context" | "workspace_route";
};

const AppHeader = () => {
  const { authStore, backendAPIStore } = useContext(MobXProviderContext);
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = useResponsive().isMobile;
  const authExperience = useAuthExperience();
  const { startTour, currentStepId, isStepActive, completeStep } = useTour();
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const actAsMenuRef = useRef<Menu>(null);
  const mobileNavigationMenuRef = useRef<Menu>(null);
  const toast = useRef<Toast>(null);
  const accountSubmenuTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [lastAuthUser, setLastAuthUser] = useState<string | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [activeAccountSubmenu, setActiveAccountSubmenu] = useState<string | null>(null);
  const [accountSubmenuDirectionByLabel, setAccountSubmenuDirectionByLabel] = useState<Record<string, "left" | "right">>({});
  const [showTutorialPicker, setShowTutorialPicker] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [providerHomesCount, setProviderHomesCount] = useState(0);
  const [headerProviderSubscribed, setHeaderProviderSubscribed] = useState(false);
  const [headerNurseAddOnEnabled, setHeaderNurseAddOnEnabled] = useState(false);
  const [relationshipUnreadCount, setRelationshipUnreadCount] = useState(0);
  const [sharedBinderCount, setSharedBinderCount] = useState(0);
  const [hasHeaderSearchAccess, setHasHeaderSearchAccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [platformInvites, setPlatformInvites] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteRoleType, setInviteRoleType] = useState(authExperience.roleOptions?.[0]?.value || ROLE_TYPE_PROVIDER);
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [roleSwitching, setRoleSwitching] = useState(false);
  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.getUserGroups()?.includes(USER_GROUP_ADMIN) || authStore.getUserRoleType() === "admin";
  const normalizedPathname = String(location.pathname || "").toLowerCase();

  const loadHeaderState = useCallback(async () => {
    try {
      const authenticated = await authStore.isUserLoggedIn();
      setLastAuthUser(authenticated ? authStore.getUserName() : null);

      const currentRoleType = authStore.getUserRoleType();
      const headerBillingRoleType = getHeaderBillingRoleType(location.pathname, currentRoleType);
      setHasHeaderSearchAccess(false);

      if (authenticated) {
        await backendAPIStore.init();
      }

      if (authenticated) {
        try {
          const subscription = await backendAPIStore.getSubscription(authStore.getUserId(), headerBillingRoleType);
          const subscriptionStatus = String(
            subscription?.subscriptionStatus || subscription?.status || "",
          ).trim().toLowerCase();
          setHasHeaderSearchAccess(
            authStore.isAdmin() || ["active", "trialing", "past_due"].includes(subscriptionStatus),
          );
          setHeaderProviderSubscribed(!!subscription?.paidSubscriptionActive);
          setHeaderNurseAddOnEnabled(!!subscription?.nurseAddOnEnabled);
        } catch (error) {
          setHasHeaderSearchAccess(authStore.isAdmin());
          setHeaderProviderSubscribed(false);
          setHeaderNurseAddOnEnabled(false);
        }
      } else {
        setHeaderProviderSubscribed(false);
        setHeaderNurseAddOnEnabled(false);
      }

      if (authenticated && headerBillingRoleType === ROLE_TYPE_PROVIDER) {
        await backendAPIStore.init();
        const response = await backendAPIStore.getHomeListForProvider();
        const homesCount = response.homes?.length || 0;
        setProviderHomesCount(homesCount);
      } else if (!authenticated) {
        setProviderHomesCount(0);
      }

      if (authenticated && canUseConnectionsAndInvites(currentRoleType)) {
        const notifications = await backendAPIStore.getRelationshipNotifications(true);
        setRelationshipUnreadCount((notifications || []).length);
        const invites = await backendAPIStore.getPlatformInvites();
        setPlatformInvites((invites || []).filter((item: any) => String(item.status || "").toLowerCase() !== "canceled"));
      }

      if (authenticated && canUseSharedContent(currentRoleType)) {
        let activeSharedCount = 0;
        let incompleteHiringRequestCount = 0;
        try {
          const sharedBinderShares = await backendAPIStore.listBinderSharesSharedWithMe();
          const activeSharedBinderShares = (sharedBinderShares || []).filter((share: any) => {
            const expirationDate = String(share?.expirationDate || "").trim();
            return !expirationDate || Number.isNaN(new Date(expirationDate).getTime()) || new Date(expirationDate).getTime() > Date.now();
          });
          activeSharedCount = activeSharedBinderShares.length;
        } catch {
          activeSharedCount = 0;
        }
        try {
          const hiringRequests = await backendAPIStore.listHiringRequests();
          incompleteHiringRequestCount = (hiringRequests || []).filter((request: any) => {
            const status = String(request?.status || "").trim().toLowerCase();
            return status !== "completed" && status !== "cancelled" && status !== "canceled";
          }).length;
        } catch {
          incompleteHiringRequestCount = 0;
        }
        setSharedBinderCount(activeSharedCount + incompleteHiringRequestCount);
      } else {
        setSharedBinderCount(0);
      }
    } catch (err) {
      console.error(err);
    }
  }, [authStore, backendAPIStore, location.pathname]);

  useEffect(() => {
    void loadHeaderState();
  }, [loadHeaderState, userRoleType]);

  // useEffect(() => {
  //   if (!highlightTutorials) {
  //     return;
  //   }

  //   const timeout = window.setTimeout(() => {
  //     setHighlightTutorials(false);
  //   }, 10000);

  //   return () => window.clearTimeout(timeout);
  // }, [highlightTutorials]);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
        setActiveAccountSubmenu(null);
      }
    };

    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, []);

  const signOut = () => {
    authStore?.signOut();
  };

  const closeTutorialPicker = () => {
    const userId = authStore.getUserId();
    if (userId) {
      localStorage.setItem(`afhhub-provider-tutorial-picker-seen:${userId}`, "true");
    }
    setShowTutorialPicker(false);
  };

  const startSelectedTutorial = async (tutorialId: TourId) => {
    closeTutorialPicker();

    if (tutorialId === "nurse_assessment") {
      await backendAPIStore.init();
      const providers = await backendAPIStore.getNurseProviders();
      const providerCount = Array.isArray(providers) ? providers.length : 0;
      const residentCount = Array.isArray(providers)
        ? providers.reduce((total: number, provider: any) => total + Number(provider?.residents?.length || 0), 0)
        : 0;
      const startStep = providerCount === 0
        ? "nurse-dashboard-add-provider-assessment"
        : residentCount === 0
          ? "nurse-dashboard-add-resident-assessment"
          : "nurse-dashboard-assessment";
      navigate(RouterAddress.NURSE_HOME);
      window.setTimeout(() => startTour("nurse_assessment", startStep), 250);
      return;
    }

    if (tutorialId === "nurse_delegation") {
      await backendAPIStore.init();
      const providers = await backendAPIStore.getNurseProviders();
      const providerCount = Array.isArray(providers) ? providers.length : 0;
      const residentCount = Array.isArray(providers)
        ? providers.reduce((total: number, provider: any) => total + Number(provider?.residents?.length || 0), 0)
        : 0;
      const startStep = providerCount === 0
        ? "nurse-dashboard-add-provider-delegation"
        : residentCount === 0
          ? "nurse-dashboard-add-resident-delegation"
          : "nurse-dashboard-delegation";
      navigate(RouterAddress.NURSE_HOME);
      window.setTimeout(() => startTour("nurse_delegation", startStep), 250);
      return;
    }

    if (tutorialId === "reminders") {
      startTour("reminders", "reminder-open-manage");
      return;
    }

    if (tutorialId === "website") {
      const startStep = providerHomesCount > 0 ? "website-open-editor" : "website-add-home";
      navigate(RouterAddress.PROVIDER_HOME);
      window.setTimeout(() => startTour("website", startStep), 250);
      return;
    }

    const startStep = providerHomesCount > 0 ? "add-resident-cta" : "add-home-cta";
    navigate(RouterAddress.PROVIDER_HOME);
    window.setTimeout(() => startTour("ncp_automation", startStep), 250);
  };

  const canUseProviderNurseWorkspace = (
    userRoleType === ROLE_TYPE_PROVIDER && headerProviderSubscribed && headerNurseAddOnEnabled
  ) || (
      isAdminUser && (normalizedPathname.startsWith("/provider/") || normalizedPathname.startsWith("/provider/nurse/"))
    );
  const isProviderNurseDashboard = canUseProviderNurseWorkspace
    && normalizedPathname.startsWith("/provider/nurse/");
  const isProviderRoute = normalizedPathname.startsWith("/provider/");
  const isOneHubForCareBrand = authExperience.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE;
  const primaryRoleType = String(
    authStore.currentUserContext?.user?.legacyRoleType
    || authStore.sessionProp.customRoleType
    || "",
  ).trim().toLowerCase();
  const profileRoleTypes: string[] = Array.from(new Set(
    (Array.isArray(authStore.currentUserContext?.profile?.selectedRoleTypes)
      ? authStore.currentUserContext?.profile?.selectedRoleTypes
      : []
    )
      .map((roleType: any) => String(roleType || "").trim().toLowerCase())
      .filter(Boolean),
  ));
  const uniqueAvailableRoles: string[] = Array.from(new Set(
    [
      primaryRoleType,
      ...profileRoleTypes,
      ...(Array.isArray(authStore.availableRoles) ? authStore.availableRoles : []),
    ]
      .map((roleType) => String(roleType || "").trim().toLowerCase())
      .filter(Boolean),
  ));
  const headerRoleOrder: Record<string, number> = {
    [ROLE_TYPE_PROVIDER]: 1,
    [ROLE_TYPE_NURSE]: 2,
    [ROLE_TYPE_CAREGIVER]: 3,
    [ROLE_TYPE_MHP]: 4,
  };
  const supportedHeaderRoles = [
    ROLE_TYPE_PROVIDER,
    ROLE_TYPE_NURSE,
    ROLE_TYPE_CAREGIVER,
    ROLE_TYPE_MHP,
  ];
  const sortedHeaderRoles = [...supportedHeaderRoles].sort((left, right) => {
    const leftRank = headerRoleOrder[left] ?? 999;
    const rightRank = headerRoleOrder[right] ?? 999;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left.localeCompare(right);
  });
  const headerWorkspaceOptions: HeaderWorkspaceOption[] = [
    ...sortedHeaderRoles
      .filter((roleType) => uniqueAvailableRoles.includes(roleType) || (isProviderRoute && roleType === ROLE_TYPE_PROVIDER))
      .map((roleType) => ({
        label: `${formatHeaderActAsLabel(roleType)}${roleType === primaryRoleType ? " (Primary)" : ""}`,
        value: `role:${roleType}`,
        roleType,
        mode: "context" as HeaderWorkspaceOption["mode"],
      })),
  ];
  const headerWorkspaceValue = (() => {
    if (canUseProviderNurseWorkspace && isProviderNurseDashboard) {
      return `role:${ROLE_TYPE_NURSE}`;
    }
    if (isProviderRoute) {
      return `role:${ROLE_TYPE_PROVIDER}`;
    }
    if (normalizedPathname === RouterAddress.MHP_HOME.toLowerCase()) {
      return `role:${ROLE_TYPE_MHP}`;
    }
    if (
      normalizedPathname === RouterAddress.NURSE_HOME.toLowerCase()
    ) {
      return `role:${ROLE_TYPE_NURSE}`;
    }
    if (normalizedPathname === RouterAddress.CAREGIVER_HOME.toLowerCase()) {
      return `role:${ROLE_TYPE_CAREGIVER}`;
    }
    const activeRoleValue = `role:${String(userRoleType || "").trim().toLowerCase()}`;
    if (headerWorkspaceOptions.some((option) => option.value === activeRoleValue)) {
      return activeRoleValue;
    }
    return activeRoleValue;
  })();
  const homePagePath = userRoleType
    ? getHeaderRoleHomePath(authStore.getAuthMode(), userRoleType)
    : RouterAddress.APP_ROOT;
  const roleLabel = isProviderRoute && isProviderNurseDashboard
    ? "Provider Nurse"
    : (isProviderRoute || userRoleType === ROLE_TYPE_PROVIDER ? "Provider" : formatRoleLabel(userRoleType));
  const visibleTutorialOptions = userRoleType === ROLE_TYPE_PROVIDER
    ? (canUseProviderNurseWorkspace && isProviderNurseDashboard
      ? nurseTutorialOptions
      : providerTutorialOptions)
    : userRoleType === ROLE_TYPE_NURSE
      ? nurseTutorialOptions
      : [];
  const isAfhHubBrand = !isOneHubForCareBrand;
  const headerLogoSrc = isAfhHubBrand ? AFH_LOGO.default : ONE_HUB_FOR_CARE_LOGO.default;
  const switchHeaderWorkspace = async (value: string) => {
    const selectedOption = headerWorkspaceOptions.find((option) => option.value === value);
    if (!selectedOption || value === headerWorkspaceValue) {
      return;
    }

    if (canUseProviderNurseWorkspace && selectedOption.roleType === ROLE_TYPE_PROVIDER) {
      navigate(RouterAddress.PROVIDER_HOME);
      return;
    }

    if (!selectedOption.roleType) {
      return;
    }

    if (authStore.getAuthMode() !== "multi_role_context") {
      navigate(getHeaderRoleHomePath(authStore.getAuthMode(), selectedOption.roleType));
      return;
    }

    setRoleSwitching(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.setCurrentUserActiveContext({
        host: window.location.host,
        brandOverride: getSessionBrandOverride(),
        membershipId: authStore.currentUserContext?.activeContext?.membershipId || undefined,
        roleType: selectedOption.roleType,
      });
      await authStore.loadUserContext(true);
      navigate(getHeaderRoleHomePath(authStore.getAuthMode(), selectedOption.roleType));
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "We could not switch workspaces right now.",
        life: 3500,
      });
    } finally {
      setRoleSwitching(false);
    }
  };
  const activeHeaderWorkspaceOption = headerWorkspaceOptions.find((option) => option.value === headerWorkspaceValue) || null;
  const actAsMenuItems: any[] = [
    ...headerWorkspaceOptions.map((option) => ({
      label: option.label,
      icon: option.value === headerWorkspaceValue ? "pi pi-check" : "pi pi-user",
      command: () => {
        void switchHeaderWorkspace(option.value);
      },
    })),
    {
      separator: true,
    },
    {
      label: "Update Profile",
      icon: "pi pi-cog",
      command: () => navigate(RouterAddress.PROFILE),
    },
  ];

  const headerItems: any[] = [];

  const refreshInviteList = async () => {
    if (!canUseConnectionsAndInvites(authStore.getUserRoleType())) {
      return;
    }

    await backendAPIStore.init();
    const invites = await backendAPIStore.getPlatformInvites();
    setPlatformInvites((invites || []).filter((item: any) => String(item.status || "").toLowerCase() !== "canceled"));
  };

  const onCreatePlatformInvite = async () => {
    const personalNote = inviteMessage.trim() || buildInviteMessage(authExperience.brandName, inviteRoleType);
    if (!inviteEmail.trim() || !inviteName.trim() || !inviteRoleType || !invitePhone.trim()) {
      toast.current?.show({ severity: "warn", summary: "Complete all fields", detail: "Email, full name, role, phone number, and personal note are required.", life: 3000 });
      return;
    }

    setInviteSubmitting(true);
    try {
      await backendAPIStore.init();
      const normalizedEmail = inviteEmail.trim().toLowerCase();
      const currentRoleType = authStore.getUserRoleType();
      if (currentRoleType !== ROLE_TYPE_CAREGIVER) {
        const existing: any = await backendAPIStore.getStaffAccount(undefined, normalizedEmail);
        if (existing?.profile?.userId || existing?.staff?.staffId) {
          toast.current?.show({ severity: "info", summary: "Account already exists", detail: "This email already has an account in Hub. Use the existing staff workflow to configure access.", life: 3500 });
          return;
        }
      }
      await backendAPIStore.saveStaffAccount({
        roleType: inviteRoleType,
        name: inviteName.trim(),
        email: normalizedEmail,
        headerInvite: true,
        staffDetails: { phoneNumber: invitePhone.trim(), personalNote },
      });
      toast.current?.show({ severity: "success", summary: "Account created", detail: `The ${authExperience.brandName} account was created and the invitation email was sent.`, life: 3000 });
      setInviteEmail("");
      setInviteName("");
      setInvitePhone("");
      setInviteMessage("");
      setInviteRoleType(authExperience.roleOptions?.[0]?.value || ROLE_TYPE_PROVIDER);
      await refreshInviteList();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3500 });
    } finally {
      setInviteSubmitting(false);
    }
  };


  const runHeaderSearch = async (page: number = 1, appendResults: boolean = false) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      toast.current?.show({ severity: "warn", summary: "Search required", detail: "Enter a name, email, or county to search.", life: 2500 });
      return;
    }

    setSearchLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.searchRegisteredNurses({
        query: trimmedQuery,
        pageSize: 50,
        page,
      });
      const nextItems = response?.items || [];
      setSearchResults((current) => appendResults ? [...current, ...nextItems] : nextItems);
      setSearchResultCount(Number(response?.totalResults || 0));
      setSearchPage(Number(response?.page || page));
      setSearchHasMore(Boolean(response?.hasMore));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3500 });
      setSearchResults([]);
      setSearchResultCount(0);
      setSearchPage(1);
      setSearchHasMore(false);
    } finally {
      setSearchLoading(false);
    }
  };

  if (canUseHeaderHome(userRoleType)) {
    headerItems.push({
      label: "Home",
      icon: "pi pi-home",
      command: () => navigate(homePagePath),
    });
  }

  if (authStore.getAuthMode() === "multi_role_context" && isAfhHubBrand) {
    headerItems.push({
      label: "Workspaces",
      icon: "pi pi-sitemap",
      command: () => navigate(RouterAddress.WORKSPACES),
    });
  }

  if (lastAuthUser && canUseSharedCalendar(userRoleType, isAdminUser)) {
    headerItems.push({
      label: "Shared Calendar",
      icon: "pi pi-calendar",
      command: () => navigate(RouterAddress.SHARED_CALENDAR),
    });
  }

  if (lastAuthUser && canUseSharedContent(userRoleType)) {
    headerItems.push({
      label: sharedBinderCount > 0 ? `Shared Content (${sharedBinderCount})` : "Shared Content",
      icon: "pi pi-share-alt",
      className: sharedBinderCount > 0 ? "app-header-shared-content-attention" : undefined,
      command: () => navigate(RouterAddress.SHARED_CONTENT_PAGE),
    });
  }

  // if (userRoleType === ROLE_TYPE_PROVIDER || userRoleType === ROLE_TYPE_CAREGIVER) {
  //   headerItems.push({
  //     label: "Reminders",
  //     icon: "pi pi-bell",
  //     className:
  //       currentStepId === "reminder-open-manage"
  //         ? "app-header-manage-tour-target"
  //         : currentStepId === "reminder-open-from-header"
  //           ? "app-header-manage-tour-target app-header-manage-tour-open"
  //           : undefined,
  //     command: () => {
  //       if (isStepActive("reminder-open-from-header")) {
  //         completeStep("reminder-open-from-header");
  //       }
  //       navigate(RouterAddress.USER_REMINDER_PAGE);
  //     },
  //   });

  // }
  if (lastAuthUser && hasHeaderSearchAccess) {
    headerItems.push({
      label: "Search",
      icon: "pi pi-search",
      items: [
        {
          label: "Registered Nurses",
          icon: "pi pi-id-card",
          command: () => setShowSearchDialog(true),
        },
      ],
    });
  }

  // if (lastAuthUser && isAfhHubBrand) {
  //   if (userRoleType === ROLE_TYPE_PROVIDER || userRoleType === ROLE_TYPE_NURSE) {
  //     headerItems.push({
  //       label: "Tutorials",
  //       icon: "pi pi-directions",
  //       command: () => setShowTutorialPicker(true),
  //       template: renderTutorialMenuItem,
  //     });
  //   }
  // }
  if (!lastAuthUser) {
    headerItems.push({
      label: "Why Use Hub",
      icon: "pi pi-bolt",
      command: () => navigate(RouterAddress.WHY_USE_HUB_PAGE),
    });
    headerItems.push({
      label: "eMAR",
      icon: "pi pi-file-medical",
      command: () => navigate(RouterAddress.EMAR_PAGE),
    });
    headerItems.push({
      label: "Features",
      icon: "pi pi-th-large",
      command: () => navigate(appendRoleQueryParam(RouterAddress.PLATFORM_FEATURES_PAGE, userRoleType)),
    });
    if (isAfhHubBrand) {
      headerItems.push({
        label: "Tutorial Library",
        icon: "pi pi-book",
        command: () => navigate(appendRoleQueryParam(RouterAddress.TUTORIALS_PAGE, userRoleType)),
      });
    }
    headerItems.push({
      label: "Pricing",
      icon: "pi pi-wallet",
      command: () => navigate(appendRoleQueryParam(RouterAddress.PRICING_PAGE, userRoleType)),
    });
  }

  // if (userRoleType === ROLE_TYPE_PROVIDER) {
  //   headerItems.push({
  //     label: "Explore",
  //     icon: "pi pi-compass",
  //     items: [
  //       {
  //         label: "Caregivers Looking For Jobs",
  //         icon: "pi pi-search",
  //         command: () => navigate(RouterAddress.FIND_CAREGIVERS),
  //       },
  //       {
  //         label: "Job Postings By Providers",
  //         icon: "pi pi-briefcase",
  //         command: () => navigate(RouterAddress.SEARCH_JOB),
  //       },
  //       {
  //         label: "AFH Listings By Providers",
  //         icon: "pi pi-home",
  //         command: () => navigate(RouterAddress.LISTINGS),
  //       },
  //     ],
  //   });
  // }

  const mobileNavigationItems: any[] = [
    ...(canUseHeaderHome(userRoleType) ? [
      { label: "Home", icon: "pi pi-home", command: () => navigate(homePagePath) },
    ] : []),
    ...(canUseSharedCalendar(userRoleType, isAdminUser) ? [
      { label: "Calendar", icon: "pi pi-calendar", command: () => navigate(RouterAddress.SHARED_CALENDAR) },
    ] : []),
    ...(lastAuthUser && canUseSharedContent(userRoleType) ? [{
      label: sharedBinderCount > 0 ? `Shared Content (${sharedBinderCount})` : "Shared Content",
      icon: "pi pi-share-alt",
      command: () => navigate(RouterAddress.SHARED_CONTENT_PAGE),
    }] : []),
    ...(canUseConnectionsAndInvites(userRoleType) ? [
      { label: "Invite", icon: "pi pi-megaphone", command: () => setShowInviteDialog(true) },
    ] : []),
  ];

  const brandLogoTarget = homePagePath || RouterAddress.APP_ROOT;
  const start = (
    <div className="app-header-brand">
      {mobile && lastAuthUser ? (
        <>
          <Button
            type="button"
            icon="pi pi-bars"
            rounded
            text
            severity="secondary"
            size="small"
            aria-label="Open navigation menu"
            tooltip="Navigation"
            tooltipOptions={{ position: "bottom" }}
            onClick={(event) => mobileNavigationMenuRef.current?.toggle(event)}
            aria-haspopup
          />
          {canUseHeaderHome(userRoleType) ? (
            <Button
              type="button"
              icon="pi pi-home"
              rounded
              text
              severity="secondary"
              size="small"
              className="app-header-mobile-home-button"
              aria-label="Home"
              tooltip="Home"
              tooltipOptions={{ position: "bottom" }}
              onClick={() => navigate(homePagePath)}
            />
          ) : null}
        </>
      ) : <div
        onClick={() => navigate(brandLogoTarget)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(brandLogoTarget);
          }
        }}
        role="button"
        tabIndex={0}
        style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}
      >
        <div className="app-header-logo-shell">
          <img alt={`${authExperience.brandName} logo`} src={headerLogoSrc} width="116" height="56" style={{ objectFit: "contain" }} />
        </div>
        <div className="app-header-brand-copy">
          <div className="app-header-brand-title">
            {isAfhHubBrand ? "AFH Hub" : "One Hub For Care"}
          </div>
          <div className="app-header-brand-subtitle">
            {lastAuthUser ? (
              userRoleType === ROLE_TYPE_PROVIDER && headerWorkspaceOptions.length > 0 ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569" }}>Acting As</span>
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Menu model={actAsMenuItems} popup ref={actAsMenuRef} popupAlignment="left" className="app-header-act-as-menu" />
                    <Button
                      type="button"
                      label={activeHeaderWorkspaceOption?.label || roleLabel}
                      icon="pi pi-chevron-down"
                      iconPos="right"
                      rounded
                      severity="warning"
                      size="small"
                      className="app-header-act-as-button"
                      disabled={roleSwitching}
                      onClick={(event) => actAsMenuRef.current?.toggle(event)}
                    />
                  </div>
                </div>
              ) : `${roleLabel} workspace`
            ) : <>One Hub for <span className="app-header-slogan-highlight">Care</span></>}
          </div>
        </div>
      </div>}
    </div>
  );

  const endMenuItemsSignedIn = [
    ...(authStore.getAuthMode() === "multi_role_context" && isAfhHubBrand ? [{
      label: "Workspaces",
      icon: "pi pi-sitemap",
      command: () => navigate(RouterAddress.WORKSPACES),
    }] : []),
    {
      label: "My Profile",
      icon: "pi pi-user",
      command: () => navigate(RouterAddress.PROFILE),
    },
    ...(authExperience.featureFlags.providerTools.eFax ? [{
      label: "eFax",
      icon: "pi pi-send",
      command: () => navigate(RouterAddress.FAX_AUDIT_HISTORY),
    }] : []),
    ...(isAdminUser ? [{
      label: "Admin Workspace",
      icon: "pi pi-shield",
      items: [
        {
          label: "Admin Dashboard",
          icon: "pi pi-shield",
          command: () => navigate(RouterAddress.ADMIN_PAGE),
        },
        {
          label: "Provider Map",
          icon: "pi pi-map",
          command: () => navigate(RouterAddress.PROVIDER_DIRECTORY_MAP),
        },
      ],
    }] : []),
    {
      label: "Help Center",
      icon: "pi pi-question-circle",
      items: [
        {
          label: "Help & Support",
          icon: "pi pi-life-ring",
          command: () => navigate(RouterAddress.SUPPORT),
        },
        {
          label: "Release Notes",
          icon: "pi pi-megaphone",
          command: () => navigate(RouterAddress.RELEASE_NOTES_PAGE),
        },
      ],
    },
    {
      label: "Billing",
      icon: "pi pi-wallet",
      command: () => navigate(RouterAddress.BILLING),
    },
    {
      label: "Legal",
      icon: "pi pi-file",
      items: [
        {
          label: "Terms of Service",
          icon: "pi pi-file",
          command: () => navigate(RouterAddress.TERMS_OF_SERVICE_PAGE),
        },
        {
          label: "Privacy Policy",
          icon: "pi pi-file",
          command: () => navigate(RouterAddress.PRIVACY_PAGE),
        },
        {
          label: "Business Associate Agreement",
          icon: "pi pi-file",
          command: () => navigate(RouterAddress.BUSINESS_ASSOCIATE_AGREEMENT_PAGE),
        },
      ],
    },
    {
      label: "Sign Out",
      icon: "pi pi-sign-out",
      command: signOut,
    },
  ];

  const closeAccountMenu = () => {
    setAccountMenuOpen(false);
    setActiveAccountSubmenu(null);
  };

  const openAccountSubmenu = (label: string) => {
    const triggerElement = accountSubmenuTriggerRefs.current[label];
    if (triggerElement) {
      const rect = triggerElement.getBoundingClientRect();
      const estimatedSubmenuWidth = 230;
      const viewportPadding = 16;
      const shouldOpenLeft = rect.right + estimatedSubmenuWidth > window.innerWidth - viewportPadding
        && rect.left - estimatedSubmenuWidth > viewportPadding;

      setAccountSubmenuDirectionByLabel((prev) => ({
        ...prev,
        [label]: shouldOpenLeft ? "left" : "right",
      }));
    }

    setActiveAccountSubmenu(label);
  };

  const onAccountItemClick = (item: any) => {
    if (item.items?.length) {
      openAccountSubmenu(item.label);
      return;
    }

    item.command?.();
    closeAccountMenu();
  };

  const renderAccountMenuItems = (items: any[], nested = false) => (
    <div className={`app-header-account-panel ${nested ? "app-header-account-submenu" : ""}`.trim()}>
      {items.map((item) => {
        const hasChildren = Boolean(item.items?.length);
        const isOpen = activeAccountSubmenu === item.label;

        return (
          <div
            key={item.label}
            className="app-header-account-item-shell"
            ref={(element) => {
              accountSubmenuTriggerRefs.current[item.label] = element;
            }}
            onMouseEnter={() => {
              if (hasChildren) {
                openAccountSubmenu(item.label);
              }
            }}
          >
            <button
              type="button"
              className={`app-header-account-item ${isOpen ? "app-header-account-item-active" : ""}`.trim()}
              onClick={() => onAccountItemClick(item)}
            >
              <span className={`app-header-account-item-icon ${item.icon}`} aria-hidden="true" />
              <span className="app-header-account-item-label">{item.label}</span>
              {hasChildren ? <span className="pi pi-angle-right app-header-account-item-chevron" aria-hidden="true" /> : null}
            </button>
            {hasChildren && isOpen ? (
              <div className={accountSubmenuDirectionByLabel[item.label] === "left" ? "app-header-account-submenu-left" : "app-header-account-submenu-right"}>
                {renderAccountMenuItems(item.items, true)}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const endMenu = lastAuthUser ? (
    <div className="app-header-actions" ref={accountMenuRef} style={mobile ? { width: "100%", position: "relative" } : undefined}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: mobile ? "0.45rem" : "0.75rem",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          position: "relative",
          width: mobile ? "100%" : undefined,
        }}
      >
        {mobile ? (
          <>
            <Button
              type="button"
              label=""
              icon="pi pi-cog"
              severity="warning"
              outlined
              rounded
              size="small"
              className="app-header-account-button"
              onClick={() => setAccountMenuOpen((current) => !current)}
              aria-haspopup
              aria-controls="app-header-account-menu"
            />
          </>
        ) : (
          <>
            {canUseConnectionsAndInvites(userRoleType) && (
              <button
                type="button"
                onClick={() => setShowInviteDialog(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.6rem 0.95rem",
                  borderRadius: "999px",
                  border: "1px solid rgba(217, 119, 6, 0.28)",
                  background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
                  color: "#9a3412",
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  boxShadow: "0 10px 24px rgba(217, 119, 6, 0.10)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="pi pi-megaphone" style={{ fontSize: "0.82rem" }} />
                <span>Invite</span>
              </button>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                padding: "0.35rem 0.45rem 0.35rem 0.85rem",
                borderRadius: "999px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
                flexWrap: "wrap",
              }}
            >
              <div className="app-header-user-summary">
                <div className="app-header-user-summary-label">{roleLabel}</div>
                <div className="app-header-user-summary-name">{lastAuthUser}</div>
              </div>
              <Button
                label="Account"
                icon="pi pi-angle-down"
                iconPos="right"
                severity="warning"
                outlined
                rounded
                className="app-header-account-button"
                onClick={() => setAccountMenuOpen((current) => !current)}
                aria-haspopup
                aria-controls="app-header-account-menu"
              />
            </div>
          </>
        )}
      </div>
      {accountMenuOpen ? (
        <div id="app-header-account-menu" className="app-header-account-menu">
          {renderAccountMenuItems(endMenuItemsSignedIn)}
        </div>
      ) : null}
      <Menu model={mobileNavigationItems} popup ref={mobileNavigationMenuRef} />
    </div>
  ) : (
    <div className="app-header-actions">
      <Button
        label="Login"
        text
        className="app-header-login-button"
        onClick={() => navigate(RouterAddress.LOGIN_PAGE)}
      />
      <Button
        label="Sign Up"
        severity="warning"
        rounded
        className="app-header-signup-button"
        onClick={() => navigate(RouterAddress.SIGN_UP)}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <header className="app-header-shell">
        <div className="app-header-frame">
          <Menubar model={mobile ? [] : headerItems} start={start} end={endMenu} className="app-header-menubar" />
        </div>
      </header>
      <Dialog
        visible={showInviteDialog}
        modal
        closable
        closeOnEscape
        draggable={false}
        resizable={false}
        style={appDialogStyle}
        header={renderSurfaceHeader(`Share ${authExperience.brandName} With Your Circle`, `Know someone who would love ${authExperience.brandName}? Send them a quick invite.`, "pi pi-megaphone")}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowInviteDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div style={appFormSectionStyle}>
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="mb-2 block">Email Address</label>
                <InputText
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter their email address"
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="mb-2 block">Full Name</label>
                <InputText
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="First and last name"
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="mb-2 block">{`Best Fit On ${authExperience.brandName}`}</label>
                <Dropdown
                  options={authExperience.roleOptions || []}
                  optionLabel="label"
                  optionValue="value"
                  value={inviteRoleType}
                  onChange={(e) => setInviteRoleType(e.value)}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="mb-2 block">Phone Number</label>
                <InputText
                  type="tel"
                  value={invitePhone}
                  onChange={(e) => setInvitePhone(e.target.value)}
                  placeholder="Enter phone number"
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-12">
                <label className="block mb-2">Personal Note</label>
                <InputTextarea
                  value={inviteMessage || buildInviteMessage(authExperience.brandName, inviteRoleType)}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={4}
                  autoResize
                  placeholder="Personal note"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div className="mt-3 flex justify-content-end">
              <Button type="button" label="Send Invite" severity="warning" outlined rounded loading={inviteSubmitting} onClick={onCreatePlatformInvite} />
            </div>
          </div>
          <div style={appNeutralPanelStyle}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Recent shares</div>
            <div className="mt-3 flex flex-column gap-2">
              {platformInvites.length === 0 ? (
                <div style={appMutedTextStyle}>{`No invites sent yet. Your next invite could bring in a new ${authExperience.brandName} member.`}</div>
              ) : platformInvites.map((invite: any) => (
                <div
                  key={invite.inviteId}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "14px",
                    padding: "0.85rem 1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                    background: "#ffffff",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{invite.targetName || invite.targetEmail}</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                      {invite.targetEmail}
                      {invite.suggestedRoleType ? ` - ${formatRoleLabel(invite.suggestedRoleType)}` : ""}
                    </div>
                  </div>
                  <div className="flex align-items-center gap-2">
                    <span>{new Date(invite.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showSearchDialog}
        modal
        closable
        closeOnEscape
        draggable={false}
        resizable={false}
        className="app-header-search-dialog"
        style={{ ...appDialogStyle, width: "min(860px, 96vw)" }}
        header={renderSurfaceHeader("Search Registered Nurses", "Find nurses by name, email, or county.", "pi pi-search")}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowSearchDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div className="app-header-search-panel" style={{ ...appNeutralPanelStyle, padding: "1rem 1rem 0.9rem" }}>
            <div className="grid">
              <div className="col-12">
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#475569", marginBottom: "0.4rem" }}>Search</label>
                <span className="p-input-icon-left app-header-search-input-shell" style={{ width: "100%" }}>
                  <i className="pi pi-search" />
                  <InputText
                    className="app-header-search-input"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchPage(1);
                      setSearchHasMore(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        void runHeaderSearch();
                      }
                    }}
                    placeholder="Search by nurse name, email, or county"
                    style={{ width: "100%", minWidth: 0 }}
                  />
                </span>
              </div>
            </div>
            <div className="app-header-search-toolbar flex justify-content-between align-items-center mt-2 gap-2 flex-wrap">
              <div className="app-header-search-helper" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                Start with nurse name, email, or county.
              </div>
              <Button type="button" label={searchLoading ? "Searching..." : "Search"} icon="pi pi-search" severity="warning" outlined rounded loading={searchLoading} onClick={() => void runHeaderSearch()} />
            </div>
          </div>

          <div style={{ ...appFormSectionStyle, padding: "1rem" }}>
            <div className="flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Results</div>
              <div style={appMutedTextStyle}>
                {searchResultCount > 0
                  ? `Showing ${searchResults.length} of ${searchResultCount} result${searchResultCount === 1 ? "" : "s"}`
                  : "No results yet"}
              </div>
            </div>

            {searchResults.length === 0 ? (
              <div
                style={{
                  ...appMutedTextStyle,
                  padding: "1rem",
                  border: "1px dashed #cbd5e1",
                  borderRadius: "14px",
                  background: "#f8fafc",
                }}
              >
                Search Registered Nurses by name, email, or county.
              </div>
            ) : (
              <div className="grid" style={{ rowGap: "0.85rem" }}>
                {searchResults.map((item, index) => (
                  <div
                    key={`${item.providerOneId || item.email || item.displayName || "nurse"}-${index}`}
                    className="col-12 md:col-6"
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "16px",
                      padding: "1.05rem 1.15rem",
                      background: "#ffffff",
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
                    }}
                  >
                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{item.displayName || "Registered Nurse"}</div>
                        <div className="mt-1" style={appMutedTextStyle}>
                          {[item.email, item.phoneNumber].filter(Boolean).join(" | ") || "No contact details available"}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          borderRadius: "999px",
                          padding: "0.25rem 0.65rem",
                          background: item.active === false ? "#fef2f2" : "#ecfdf5",
                          color: item.active === false ? "#b91c1c" : "#166534",
                          fontSize: "0.76rem",
                          fontWeight: 700,
                        }}
                      >
                        {item.active === false ? "Inactive" : "Active"}
                      </div>
                    </div>
                    <div className="mt-2" style={{ fontSize: "0.82rem", color: "#334155", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                      {item.providerOneId ? <div><strong>ProviderOne ID:</strong> {item.providerOneId}</div> : null}
                      {item.countiesRaw ? <div><strong>County:</strong> {item.countiesRaw}</div> : null}
                      {item.faxNumber ? <div><strong>Fax:</strong> {item.faxNumber}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {searchResults.length > 0 && searchHasMore ? (
              <div className="mt-3 flex justify-content-center">
                <Button
                  type="button"
                  label={searchLoading ? "Loading..." : "Load More"}
                  icon="pi pi-plus"
                  severity="warning"
                  outlined
                  rounded
                  loading={searchLoading}
                  onClick={() => void runHeaderSearch(searchPage + 1, true)}
                />
              </div>
            ) : null}
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showTutorialPicker}
        modal
        closable
        closeOnEscape
        draggable={false}
        resizable={false}
        style={appDialogStyle}
        header={renderSurfaceHeader("Guided Tutorials", "Choose a workflow and follow the steps on screen.", "pi pi-directions")}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={closeTutorialPicker}
      >
        <div className="flex flex-column gap-3">
          <div style={appAccentPanelStyle}>
            <div style={{ fontWeight: 700, color: "#0f3d63" }}>A good place to start</div>
            <div className="mt-2" style={appMutedTextStyle}>
              If you are new here, start with the guided flow that helps you automate the resident assessment, care plan, and delegation forms. You can come back any time and try the other tutorials from the header.
            </div>
          </div>
          {visibleTutorialOptions.map((option) => (
            <div key={option.id} style={{ ...appNeutralPanelStyle, boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)" }}>
              <div className="flex flex-wrap justify-content-between align-items-start gap-3">
                <div style={{ maxWidth: "34rem" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "0.6rem", fontWeight: 700, color: "#0f172a" }}>
                    <span className={option.icon} style={{ color: "#185DA2" }} />
                    <span>{option.title}</span>
                  </div>
                  <div className="mt-2" style={appMutedTextStyle}>
                    {option.description}
                  </div>
                </div>
                <Button
                  type="button"
                  label="Start Guide"
                  severity="warning"
                  rounded
                  onClick={() => { void startSelectedTutorial(option.id); }}
                />
              </div>
            </div>
          ))}
        </div>
      </Dialog>
    </>
  );
};

export default observer(AppHeader);
